import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { MaterialModule } from '../../shared/material.module';
import { EmployeesService } from '../../services/employees.service';
import { AuthService } from '../../services/auth.service';
import { Router } from '@angular/router';
import { ExcelTemplateService } from '../../services/excel-template.service';
import * as XLSX from 'xlsx';
import { BreadcrumbComponent } from 'src/app/components/shared/breadcrumb/breadcrumb.component';
import {
  getFirestore,
  collection,
  query,
  where,
  getDocs,
  limit
} from '@angular/fire/firestore';

interface Employee {
  id: string;
  email: string;
  fullName: string;
  handler: string;
  phone: string;
  profilePicture: string;
  department?: string;
  position?: string;
  status?: 'active' | 'inactive' | 'on_leave' | 'terminated';
  joinDate?: string;
  createdAt?: string;
}

type ViewMode = 'gallery' | 'table';

@Component({
  selector: 'app-employees',
  standalone: true,
  imports: [CommonModule, FormsModule, MaterialModule, BreadcrumbComponent],
  templateUrl: './employees.component.html'
})
export class EmployeesComponent implements OnInit {
  employees: Employee[] = [];
  loading = true;
  error: string | null = null;
  currentUser: any;
  viewMode: ViewMode = 'gallery';
  currentView: string = 'gallery'; // For mat-button-toggle-group
  deleting: Set<string> = new Set(); // Track which employees are being deleted
  mainHandler: string = ''; // Store the main handler for navigation
  
  // Material Table columns
  displayedColumns: string[] = ['photo', 'name', 'handler', 'email', 'phone', 'department', 'position', 'status', 'id', 'actions'];
  formatColumns: string[] = ['fullName', 'email', 'handler', 'phone', 'department'];
  formatExample: any[] = [{}]; // Dummy data for format table
  
  // Add Employee Form
  showAddForm = false;
  saving = false;
  newEmployee: Partial<Employee> = {};
  
  // Import Dialog
  showImportDialog = false;
  importing = false;
  selectedFile: File | null = null;
  isDragOver = false;
  importErrors: string[] = [];
  
  // Table configuration
  tableConfig: any = null;

  constructor(
    private employeesService: EmployeesService,
    private authService: AuthService,
    private router: Router
  ) {}

  ngOnInit() {
    this.currentUser = this.authService.currentUser;
    
    if (!this.currentUser) {
      console.error('No authenticated user found');
      this.router.navigate(['/login']);
      return;
    }

    console.log('Current user:', this.currentUser);
    this.loadTableConfig();
    this.loadMainHandler();
    this.loadEmployees();
  }

  async loadTableConfig() {
    try {
      const response = await fetch('/assets/json/app/table/employees.json');
      this.tableConfig = await response.json();
      console.log('Table config loaded:', this.tableConfig);
    } catch (error) {
      console.error('Error loading table config:', error);
    }
  }

  async loadMainHandler() {
    try {
      if (!this.currentUser) {
        console.warn('No current user found for loading main handler');
        return;
      }

      const userHandler = this.currentUser.userId || this.currentUser.uid;
      console.log('Loading main handler for user:', userHandler);

      const db = getFirestore();
      
      // Search in users collection to get the user's email
      const usersRef = collection(db, 'users');
      const userQuery = query(usersRef, where('uid', '==', userHandler));
      const userSnapshot = await getDocs(userQuery);
      
      let userEmail = '';
      if (!userSnapshot.empty) {
        const userData = userSnapshot.docs[0].data();
        userEmail = userData['email'] || '';
        console.log('Found user with email:', userEmail);
      }
      
      // Search for the user's main card
      const cardsRef = collection(db, 'data/digitalcard/cards');
      
      // Try with the userHandler (uid) first
      const cardQuery1 = query(cardsRef, where('handler', '==', userHandler));
      const cardSnapshot1 = await getDocs(cardQuery1);
      
      if (!cardSnapshot1.empty) {
        const cardData = cardSnapshot1.docs[0].data();
        this.mainHandler = cardData['handler'] || userHandler;
        console.log('Found main handler via uid:', this.mainHandler);
        return;
      }
      
      // Try with email if uid didn't work
      if (userEmail) {
        const cardQuery2 = query(cardsRef, where('handler', '==', userEmail));
        const cardSnapshot2 = await getDocs(cardQuery2);
        
        if (!cardSnapshot2.empty) {
          const cardData = cardSnapshot2.docs[0].data();
          this.mainHandler = cardData['handler'] || userEmail;
          console.log('Found main handler via email:', this.mainHandler);
          return;
        }
        
        // Try searching by email field
        const cardQuery3 = query(cardsRef, where('email', '==', userEmail));
        const cardSnapshot3 = await getDocs(cardQuery3);
        
        if (!cardSnapshot3.empty) {
          const cardData = cardSnapshot3.docs[0].data();
          this.mainHandler = cardData['handler'] || userEmail;
          console.log('Found main handler via email field:', this.mainHandler);
          return;
        }
      }
      
      // Fallback to userHandler if no card found
      this.mainHandler = userHandler;
      console.log('Using fallback main handler:', this.mainHandler);
      
    } catch (error) {
      console.error('Error loading main handler:', error);
      // Fallback to userHandler
      this.mainHandler = this.currentUser?.userId || this.currentUser?.uid || '';
    }
  }

  async loadEmployees() {
    try {
      this.loading = true;
      this.error = null;
      
      // Get the current user's handler to find their card
      const userHandler = this.currentUser.userId || this.currentUser.uid;
      console.log('Loading employees for user handler:', userHandler);
      
      this.employees = await this.employeesService.getEmployeesByHandler(userHandler);
      console.log('Loaded employees:', this.employees);
      
    } catch (error) {
      console.error('Error loading employees:', error);
      this.error = 'Failed to load employees. Please try again.';
    } finally {
      this.loading = false;
    }
  }

  refresh() {
    this.loadEmployees();
  }

  trackByEmployeeId(index: number, employee: Employee): string {
    return employee.id;
  }

  toggleView(mode: ViewMode | string) {
    this.viewMode = mode as ViewMode;
    this.currentView = mode as string;
  }

  isGalleryView(): boolean {
    return this.viewMode === 'gallery';
  }

  isTableView(): boolean {
    return this.viewMode === 'table';
  }

  async deleteEmployee(employee: Employee) {
    const confirmed = confirm(`Are you sure you want to delete ${employee.fullName || 'this employee'}? This action cannot be undone.`);
    
    if (!confirmed) {
      return;
    }

    try {
      this.deleting.add(employee.id);
      console.log('Deleting employee:', employee);
      
      // Get the current user's handler to find their card
      const userHandler = this.currentUser.userId || this.currentUser.uid;
      
      // Find the card ID for this user
      const cardId = await this.employeesService.findEmployeeCardId(userHandler, employee.id);
      
      if (!cardId) {
        throw new Error('Could not find the parent card for this employee');
      }
      
      // Delete the employee
      const success = await this.employeesService.deleteEmployee(cardId, employee.id);
      
      if (success) {
        // Remove from local array
        this.employees = this.employees.filter(emp => emp.id !== employee.id);
        console.log('Employee deleted successfully');
      } else {
        throw new Error('Failed to delete employee from database');
      }
      
    } catch (error) {
      console.error('Error deleting employee:', error);
      alert(`Failed to delete employee: ${error instanceof Error ? error.message : 'Unknown error'}`);
    } finally {
      this.deleting.delete(employee.id);
    }
  }

  isDeleting(employeeId: string): boolean {
    return this.deleting.has(employeeId);
  }

  viewCard(employee: Employee) {
    console.log('Attempting to view card for employee:', employee);
    
    if (!employee.handler) {
      alert('Employee does not have a handler assigned. Please edit the employee to add a handler.');
      return;
    }

    if (!this.mainHandler) {
      alert('Main handler not found. Please try refreshing the page.');
      console.error('Main handler is empty:', this.mainHandler);
      return;
    }

    // Navigate to the URL pattern: localhost:4200/{mainHandler}/{subhandler}
    const targetUrl = `http://localhost:4200/${this.mainHandler}/${employee.handler}`;
    console.log('Navigating to view card:', targetUrl);
    console.log('Main handler:', this.mainHandler);
    console.log('Employee handler (subhandler):', employee.handler);
    
    // Open in new tab/window
    try {
      window.open(targetUrl, '_blank');
    } catch (error) {
      console.error('Error opening card view:', error);
      alert('Unable to open the card view. Please try again.');
    }
  }

  getStatusLabel(status: string): string {
    const statusMap: { [key: string]: string } = {
      'active': 'Active',
      'inactive': 'Inactive',
      'on_leave': 'On Leave',
      'terminated': 'Terminated'
    };
    return statusMap[status] || 'Active';
  }

  getStatusColor(status: string): 'primary' | 'accent' | 'warn' | undefined {
    const colorMap: { [key: string]: 'primary' | 'accent' | 'warn' | undefined } = {
      'active': 'primary',
      'inactive': undefined,
      'on_leave': 'accent',
      'terminated': 'warn'
    };
    return colorMap[status] || 'primary';
  }

  // Add Employee Form Methods
  openAddEmployeeForm() {
    this.showAddForm = true;
    this.newEmployee = {
      fullName: '',
      email: '',
      handler: '',
      phone: '',
      profilePicture: '',
      department: '',
      position: '',
      status: 'active',
      joinDate: ''
    };
  }

  closeAddEmployeeForm() {
    this.showAddForm = false;
    this.newEmployee = {};
    this.saving = false;
  }

  async saveEmployee() {
    if (!this.newEmployee.fullName || !this.newEmployee.email || !this.newEmployee.handler) {
      alert('Please fill in all required fields (Full Name, Email, Handler)');
      return;
    }

    try {
      this.saving = true;
      
      // Get the current user's handler to find their card
      const userHandler = this.currentUser.userId || this.currentUser.uid;
      
      // Find the card ID for this user
      const cardId = await this.employeesService.findEmployeeCardId(userHandler, '');
      
      if (!cardId) {
        throw new Error('Could not find your company card. Please contact support.');
      }
      
      // Generate a unique ID for the new employee
      const newEmployeeId = 'emp_' + Date.now() + '_' + Math.random().toString(36).substr(2, 9);
      
      // Prepare employee data
      const employeeData: Employee = {
        id: newEmployeeId,
        fullName: this.newEmployee.fullName || '',
        email: this.newEmployee.email || '',
        handler: this.newEmployee.handler || '',
        phone: this.newEmployee.phone || '',
        profilePicture: this.newEmployee.profilePicture || '',
        department: this.newEmployee.department || '',
        position: this.newEmployee.position || '',
        status: (this.newEmployee.status as any) || 'active',
        joinDate: this.newEmployee.joinDate || '',
        createdAt: new Date().toISOString()
      };
      
      // Save to Firestore
      const success = await this.employeesService.addEmployee(cardId, newEmployeeId, employeeData);
      
      if (success) {
        // Add to local array
        this.employees.push(employeeData);
        this.closeAddEmployeeForm();
        console.log('Employee added successfully');
      } else {
        throw new Error('Failed to save employee to database');
      }
      
    } catch (error) {
      console.error('Error saving employee:', error);
      alert(`Failed to save employee: ${error instanceof Error ? error.message : 'Unknown error'}`);
    } finally {
      this.saving = false;
    }
  }

  // Import Dialog Methods
  openImportDialog() {
    this.showImportDialog = true;
    this.selectedFile = null;
    this.importing = false;
    this.importErrors = [];
  }

  closeImportDialog() {
    this.showImportDialog = false;
    this.selectedFile = null;
    this.importing = false;
    this.importErrors = [];
  }

  handleFileInput(input: HTMLInputElement) {
    const file = input.files?.[0];
    if (file) {
      this.selectedFile = file;
    }
  }

  triggerFileInput(input: HTMLInputElement) {
    input.click();
    // Add event listener for file selection
    input.addEventListener('change', () => {
      this.handleFileInput(input);
    }, { once: true });
  }

  onDragOver(event: DragEvent) {
    event.preventDefault();
    event.stopPropagation();
    this.isDragOver = true;
  }

  onDragLeave(event: DragEvent) {
    event.preventDefault();
    event.stopPropagation();
    this.isDragOver = false;
  }

  onDrop(event: DragEvent) {
    event.preventDefault();
    event.stopPropagation();
    this.isDragOver = false;

    const files = event.dataTransfer?.files;
    if (files && files.length > 0) {
      const file = files[0];
      if (this.isValidFileType(file)) {
        this.selectedFile = file;
      } else {
        alert('Please select a valid Excel or CSV file (.xlsx, .xls, .csv)');
      }
    }
  }

  private isValidFileType(file: File): boolean {
    const validTypes = [
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet', // .xlsx
      'application/vnd.ms-excel', // .xls
      'text/csv' // .csv
    ];
    const validExtensions = ['.xlsx', '.xls', '.csv'];
    
    return validTypes.includes(file.type) || 
           validExtensions.some(ext => file.name.toLowerCase().endsWith(ext));
  }

  removeSelectedFile() {
    this.selectedFile = null;
  }

  formatFileSize(bytes: number): string {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  }

  async importEmployees() {
    if (!this.selectedFile) {
      alert('Please select a file to import');
      return;
    }

    try {
      this.importing = true;
      
      // Get the current user's handler to find their card
      const userHandler = this.currentUser.userId || this.currentUser.uid;
      
      // Find the card ID for this user
      const cardId = await this.employeesService.findEmployeeCardId(userHandler, '');
      
      if (!cardId) {
        throw new Error('Could not find your company card. Please contact support.');
      }
      
      // Parse the Excel file
      const employeesData = await this.parseExcelFile(this.selectedFile);
      
      if (employeesData.length === 0) {
        throw new Error('No valid employee data found in the file');
      }
      
      // Import employees one by one
      let successCount = 0;
      let errorCount = 0;
      
        for (const employeeData of employeesData) {
          try {
            const newEmployeeId = 'emp_' + Date.now() + '_' + Math.random().toString(36).substr(2, 9);
            const fullEmployeeData: Employee = {
              id: newEmployeeId,
              fullName: employeeData.fullName || '',
              email: employeeData.email || '',
              handler: employeeData.handler || '',
              phone: employeeData.phone || '',
              profilePicture: employeeData.profilePicture || '',
              department: employeeData.department || '',
              position: employeeData.position || '',
              status: (employeeData.status as any) || 'active',
              joinDate: employeeData.joinDate || '',
              createdAt: new Date().toISOString()
            };          const success = await this.employeesService.addEmployee(cardId, newEmployeeId, fullEmployeeData);
          
          if (success) {
            this.employees.push(fullEmployeeData);
            successCount++;
          } else {
            errorCount++;
          }
        } catch (error) {
          console.error('Error importing employee:', employeeData, error);
          errorCount++;
        }
      }
      
      this.closeImportDialog();
      alert(`Import completed! Successfully imported ${successCount} employees. ${errorCount > 0 ? `${errorCount} errors occurred.` : ''}`);
      
    } catch (error) {
      console.error('Error importing employees:', error);
      alert(`Failed to import employees: ${error instanceof Error ? error.message : 'Unknown error'}`);
    } finally {
      this.importing = false;
    }
  }

  private async parseExcelFile(file: File): Promise<Partial<Employee>[]> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      
      reader.onload = (e) => {
        try {
          const data = e.target?.result;
          let employees: Partial<Employee>[] = [];
          
          if (file.name.toLowerCase().endsWith('.csv')) {
            // Parse CSV
            employees = this.parseCSV(data as string);
          } else {
            // Parse Excel using XLSX library
            const workbook = XLSX.read(data, { type: 'array' });
            const firstSheetName = workbook.SheetNames[0];
            const worksheet = workbook.Sheets[firstSheetName];
            
            // Convert to JSON
            const jsonData = XLSX.utils.sheet_to_json(worksheet, { header: 1 });
            employees = this.parseExcelData(jsonData as any[][]);
          }
          
          resolve(employees);
        } catch (error) {
          reject(error);
        }
      };
      
      reader.onerror = () => reject(new Error('Failed to read file'));
      
      if (file.name.toLowerCase().endsWith('.csv')) {
        reader.readAsText(file);
      } else {
        reader.readAsArrayBuffer(file);
      }
    });
  }

  private parseExcelData(data: any[][]): Partial<Employee>[] {
    if (data.length < 2) {
      throw new Error('Excel file must have at least a header row and one data row');
    }
    
    const headers = data[0].map((h: any) => h?.toString().trim().toLowerCase() || '');
    const employees: Partial<Employee>[] = [];
    this.importErrors = [];
    
    for (let i = 1; i < data.length; i++) {
      const row = data[i];
      if (!row || row.length === 0) continue;
      
      const employee: Partial<Employee> = {};
      let hasRequiredFields = false;
      
      headers.forEach((header, index) => {
        const value = row[index]?.toString().trim() || '';
        
        switch (header) {
          case 'fullname':
          case 'full_name':
          case 'name':
          case 'full name':
            employee.fullName = value;
            hasRequiredFields = !!value;
            break;
          case 'email':
          case 'email_address':
          case 'email address':
            employee.email = value;
            hasRequiredFields = hasRequiredFields && !!value;
            break;
          case 'handler':
          case 'username':
          case 'user_name':
          case 'user name':
            employee.handler = value;
            hasRequiredFields = hasRequiredFields && !!value;
            break;
          case 'phone':
          case 'phonenumber':
          case 'phone_number':
          case 'phone number':
          case 'mobile':
            employee.phone = value;
            break;
          case 'profilepicture':
          case 'profile_picture':
          case 'profile picture':
          case 'photo':
          case 'avatar':
          case 'image':
            employee.profilePicture = value;
            break;
          case 'department':
          case 'dept':
            employee.department = value;
            break;
          case 'position':
          case 'job_title':
          case 'job title':
          case 'title':
          case 'role':
            employee.position = value;
            break;
          case 'status':
          case 'employee_status':
          case 'employment_status':
            const statusValue = value.toLowerCase();
            if (['active', 'inactive', 'on_leave', 'terminated'].includes(statusValue)) {
              employee.status = statusValue as any;
            } else {
              employee.status = 'active';
            }
            break;
          case 'joindate':
          case 'join_date':
          case 'join date':
          case 'start_date':
          case 'start date':
          case 'hiring_date':
          case 'date_of_joining':
            if (value) {
              // Try to parse date
              try {
                const date = new Date(value);
                if (!isNaN(date.getTime())) {
                  employee.joinDate = date.toISOString().split('T')[0];
                }
              } catch {
                // Invalid date, skip
              }
            }
            break;
        }
      });
      
      // Validate required fields
      if (employee.fullName && employee.email && employee.handler) {
        // Additional validation
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        const handlerRegex = /^[a-zA-Z0-9_]+$/;
        
        if (!emailRegex.test(employee.email)) {
          this.importErrors.push(`Row ${i + 1}: Invalid email format - ${employee.email}`);
          continue;
        }
        
        if (!handlerRegex.test(employee.handler)) {
          this.importErrors.push(`Row ${i + 1}: Invalid handler format - ${employee.handler} (only letters, numbers, and underscores allowed)`);
          continue;
        }
        
        employees.push(employee);
      } else {
        this.importErrors.push(`Row ${i + 1}: Missing required fields (fullName, email, handler)`);
      }
    }
    
    return employees;
  }

  private parseCSV(csvData: string): Partial<Employee>[] {
    const lines = csvData.split('\n');
    if (lines.length < 2) {
      throw new Error('CSV file must have at least a header row and one data row');
    }
    
    const headers = lines[0].split(',').map(h => h.trim().toLowerCase());
    const employees: Partial<Employee>[] = [];
    this.importErrors = [];
    
    for (let i = 1; i < lines.length; i++) {
      const line = lines[i].trim();
      if (!line) continue;
      
      const values = line.split(',').map(v => v.trim().replace(/"/g, ''));
      if (values.length < headers.length) continue;
      
      const employee: Partial<Employee> = {};
      
      headers.forEach((header, index) => {
        const value = values[index] || '';
        
        switch (header) {
          case 'fullname':
          case 'full_name':
          case 'name':
          case 'full name':
            employee.fullName = value;
            break;
          case 'email':
          case 'email_address':
          case 'email address':
            employee.email = value;
            break;
          case 'handler':
          case 'username':
          case 'user_name':
          case 'user name':
            employee.handler = value;
            break;
          case 'phone':
          case 'phonenumber':
          case 'phone_number':
          case 'phone number':
          case 'mobile':
            employee.phone = value;
            break;
          case 'profilepicture':
          case 'profile_picture':
          case 'profile picture':
          case 'photo':
          case 'avatar':
          case 'image':
            employee.profilePicture = value;
            break;
          case 'department':
          case 'dept':
            employee.department = value;
            break;
          case 'position':
          case 'job_title':
          case 'job title':
          case 'title':
          case 'role':
            employee.position = value;
            break;
          case 'status':
          case 'employee_status':
          case 'employment_status':
            const statusValue = value.toLowerCase();
            if (['active', 'inactive', 'on_leave', 'terminated'].includes(statusValue)) {
              employee.status = statusValue as any;
            } else {
              employee.status = 'active';
            }
            break;
          case 'joindate':
          case 'join_date':
          case 'join date':
          case 'start_date':
          case 'start date':
          case 'hiring_date':
          case 'date_of_joining':
            if (value) {
              try {
                const date = new Date(value);
                if (!isNaN(date.getTime())) {
                  employee.joinDate = date.toISOString().split('T')[0];
                }
              } catch {
                // Invalid date, skip
              }
            }
            break;
        }
      });
      
      // Validate required fields
      if (employee.fullName && employee.email && employee.handler) {
        // Additional validation
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        const handlerRegex = /^[a-zA-Z0-9_]+$/;
        
        if (!emailRegex.test(employee.email)) {
          this.importErrors.push(`Row ${i + 1}: Invalid email format - ${employee.email}`);
          continue;
        }
        
        if (!handlerRegex.test(employee.handler)) {
          this.importErrors.push(`Row ${i + 1}: Invalid handler format - ${employee.handler} (only letters, numbers, and underscores allowed)`);
          continue;
        }
        
        employees.push(employee);
      } else {
        this.importErrors.push(`Row ${i + 1}: Missing required fields (fullName, email, handler)`);
      }
    }
    
    return employees;
  }

  // Export functionality
  exportEmployees(format: 'csv' | 'excel' = 'excel') {
    if (this.employees.length === 0) {
      alert('No employees to export');
      return;
    }

    const exportData = this.employees.map(emp => ({
      'Full Name': emp.fullName,
      'Email': emp.email,
      'Handler': emp.handler,
      'Phone': emp.phone,
      'Department': emp.department || '',
      'Position': emp.position || '',
      'Status': emp.status || 'active',
      'Join Date': emp.joinDate || '',
      'Profile Picture': emp.profilePicture || ''
    }));

    if (format === 'csv') {
      this.exportToCSV(exportData);
    } else {
      this.exportToExcel(exportData);
    }
  }

  private exportToCSV(data: any[]) {
    const headers = Object.keys(data[0]);
    const csvContent = [
      headers.join(','),
      ...data.map(row => headers.map(header => `"${row[header] || ''}"`).join(','))
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', `employees_${new Date().toISOString().split('T')[0]}.csv`);
    link.classList.add('invisible');
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  }

  private exportToExcel(data: any[]) {
    const worksheet = XLSX.utils.json_to_sheet(data);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Employees');
    
    // Auto-resize columns
    const colWidths = Object.keys(data[0]).map(key => {
      const maxLength = Math.max(
        key.length,
        ...data.map(row => (row[key] || '').toString().length)
      );
      return { wch: Math.min(maxLength + 2, 50) };
    });
    worksheet['!cols'] = colWidths;
    
    XLSX.writeFile(workbook, `employees_${new Date().toISOString().split('T')[0]}.xlsx`);
  }

  // Download sample template
  downloadSampleTemplate() {
    ExcelTemplateService.generateEmployeeTemplate();
  }
}
